import { CvpLocale } from "../constants/CvpLocale";
import { useEvaluatedLocale } from "../localization/useEvaluatedLocale";
import { useSettings } from "./useSettings";

export interface ExtraLinkTranslation {
  lang: string;
  text: string;
  url: string;
}

/*
Convert text like below into ExtraLinkTranslation[]

NB|Personalhåndbok|https://example.com?lang=nb
EN|Employee handbook|https://example.com?lang=en
SV|Anställd handbok|https://example.com?lang=sv
DA|Medarbejderhåndbog|https://example.com?lang=da
FR|Manuel de l'employé|https://example.com?lang=fr
PL|Podręcznik pracownika|https://example.com?lang=pl
DE|Mitarbeiterhandbuch|https://example.com?lang=de
*/
export function parseSingleExtraLink(
  extraLinks: string
): ExtraLinkTranslation[] {
  return extraLinks
    .split("\n")
    .map((t) => t.trim())
    .filter((t) => t)
    .map((link) => {
      const [lang, text, url] = link.split("|");
      if (!lang || !text || !url) {
        return null;
      }

      if (!Object.values(CvpLocale).includes(lang.toLowerCase() as CvpLocale)) {
        return null;
      }

      return {
        lang: lang.toLowerCase(),
        text: text,
        url: url,
      };
    });
}

export const EXTRA_LINKS_SEPARATOR = "------\n";

/*
Convert text like below into ExtraLinkTranslation[][]

NB|Personalhåndbok|https://example.com?lang=nb
EN|Employee handbook|https://example.com?lang=en
SV|Anställd handbok|https://example.com?lang=sv
DA|Medarbejderhåndbog|https://example.com?lang=da
FR|Manuel de l'employé|https://example.com?lang=fr
PL|Podręcznik pracownika|https://example.com?lang=pl
DE|Mitarbeiterhandbuch|https://example.com?lang=de
------\n
NB|Personalhåndbok|https://example.com?lang=nb
EN|Employee handbook|https://example.com?lang=en
SV|Anställd handbok|https://example.com?lang=sv
DA|Medarbejderhåndbog|https://example.com?lang=da
FR|Manuel de l'employé|https://example.com?lang=fr
PL|Podręcznik pracownika|https://example.com?lang=pl
DE|Mitarbeiterhandbuch|https://example.com?lang=de
*/
export function parseExtraLinks(extraLinks: string): ExtraLinkTranslation[][] {
  return extraLinks.split(EXTRA_LINKS_SEPARATOR).map(parseSingleExtraLink);
}

/**
 * Get extra links for the current locale, if not found, return the English version
 */
export function useExtraLinks() {
  const settings = useSettings();
  const locale = useEvaluatedLocale();
  const parsedExtraLinks = parseExtraLinks(settings.menu_extraLinks ?? "");

  return parsedExtraLinks
    .map((links) => {
      let link = links.find((l) => l && l.lang === locale);

      if (!link) {
        link = links.find((l) => l && l.lang === CvpLocale.en);
      }

      return link;
    })
    .filter((l) => l);
}
